import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import SpotifySearch from './SpotifySearch';
import YouTubeSearch from './YouTubeSearch';
import Login from './Login';

// Embedded YouTube Player for the floating player
// Uses a key-based approach to avoid YouTube API destroy errors
function EmbeddedYouTubePlayer({ videoId, onPlayerReady, onStateChange, volume = 50 }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const ytTargetRef = useRef(null);
  const isPlayingRef = useRef(false);
  const mountedRef = useRef(true);
  const volumeRef = useRef(volume); // Use ref to avoid re-creating player on volume change
  const onPlayerReadyRef = useRef(onPlayerReady); // Ref for callback to avoid effect re-runs
  const onStateChangeRef = useRef(onStateChange); // Ref for callback to avoid effect re-runs
  const [playerReady, setPlayerReady] = useState(false);
  const [playerKey, setPlayerKey] = useState(0); // Force new div when video changes

  // Keep callback refs in sync
  useEffect(() => {
    onPlayerReadyRef.current = onPlayerReady;
    onStateChangeRef.current = onStateChange;
  }, [onPlayerReady, onStateChange]);

  // Keep volume ref in sync
  useEffect(() => {
    volumeRef.current = volume;
    // Also update player volume if it exists
    if (ytTargetRef.current) {
      try { ytTargetRef.current.setVolume(volume); } catch (e) { /* ignore */ }
    }
  }, [volume]);

  // Initialize YouTube IFrame API once
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = () => {
        console.log('YouTube API ready');
      };
    }
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reset player when videoId changes
  useEffect(() => {
    setPlayerReady(false);
    setPlayerKey(k => k + 1);
  }, [videoId]);

  // Create player after key changes (new div is ready)
  useEffect(() => {
    if (!videoId || !containerRef.current) return;

    let checkYTReady = null;
    let cancelled = false;

    checkYTReady = setInterval(() => {
      if (window.YT && window.YT.Player && containerRef.current && !cancelled) {
        clearInterval(checkYTReady);
        checkYTReady = null;
        createPlayer();
      }
    }, 100);

    function createPlayer() {
      if (cancelled || !mountedRef.current || !containerRef.current) return;

      try {
        playerRef.current = new window.YT.Player(containerRef.current, {
          height: '80',
          width: '80',
          videoId: videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
          },
          events: {
            onReady: (event) => {
              if (cancelled || !mountedRef.current) return;
              
              ytTargetRef.current = event.target;
              try { event.target.setVolume(volumeRef.current); } catch (e) { /* ignore */ }
              setPlayerReady(true);
              
              // Create wrapper that uses refs directly (not closures that can become stale)
              const playerWrapper = {
                play: () => {
                  // Always try to play
                  try { ytTargetRef.current?.playVideo(); } catch (e) { /* ignore */ }
                },
                pause: () => {
                  // Always try to pause
                  try { ytTargetRef.current?.pauseVideo(); } catch (e) { /* ignore */ }
                },
                stop: () => {
                  try { ytTargetRef.current?.stopVideo(); } catch (e) { /* ignore */ }
                },
                isPlaying: () => isPlayingRef.current,
                getCurrentTime: () => {
                  try { return ytTargetRef.current?.getCurrentTime() || 0; } catch (e) { return 0; }
                },
                getDuration: () => {
                  try { return ytTargetRef.current?.getDuration() || 0; } catch (e) { return 0; }
                },
                setVolume: (vol) => {
                  try { ytTargetRef.current?.setVolume(vol); } catch (e) { /* ignore */ }
                },
                getVolume: () => {
                  try { return ytTargetRef.current?.getVolume() || 0; } catch (e) { return 0; }
                },
                destroy: () => {} // No-op, let React handle cleanup
              };
              
              // Use ref to get latest callback without effect dependency
              if (onPlayerReadyRef.current) onPlayerReadyRef.current(playerWrapper);
            },
            onStateChange: (event) => {
              if (cancelled || !mountedRef.current) return;
              const playing = event.data === 1;
              isPlayingRef.current = playing;
              // Use ref to get latest callback without effect dependency
              if (onStateChangeRef.current) onStateChangeRef.current(playing);
            },
            onError: (event) => {
              console.error('YouTube player error:', event.data);
            }
          },
        });
      } catch (error) {
        console.error('Error creating YouTube player:', error);
      }
    }

    return () => {
      cancelled = true;
      if (checkYTReady) clearInterval(checkYTReady);
      // Don't null out ytTargetRef here - we need it for pause() to work after cleanup
      // The old iframe will be removed from DOM when key changes
      playerRef.current = null;
    };
  }, [videoId, playerKey]); // Callbacks accessed via refs to avoid re-runs

  return (
    <div className="embedded-youtube-player">
      {/* Key forces a new div element for each video, avoiding destroy issues */}
      <div key={`yt-${videoId}-${playerKey}`} ref={containerRef}></div>
      {!playerReady && (
        <div className="youtube-loading">▶</div>
      )}
    </div>
  );
}

function UnifiedFloatingPlayer({
  service,
  onServiceChange,
  onBeforeSwitchToSpotify,
  onBeforeSwitchToYoutube,
  spotifyEnabled,
  spotifyPlayer,
  spotifyTrack,
  isSpotifyPaused,
  spotifyDeviceId,
  youtubeVideo,
  isYoutubePlaying,
  onYoutubeVideoSelect,
  onYoutubePlayerReady,
  onYoutubeStateChange,
  youtubeApiError,
  onYoutubeSearchError,
  onMaximizeSearch
}) {
  const [isActive, setIsActive] = useState(false);
  const [volume, setVolume] = useState(50);
  const [showVolumeControls, setShowVolumeControls] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [notification, setNotification] = useState(null);
  const [youtubePlayer, setYoutubePlayer] = useState(null); // Internal YouTube player reference
  const [youtubeActuallyPlaying, setYoutubeActuallyPlaying] = useState(false); // Actual player state

  // Handle YouTube player ready - update internal state and notify parent
  // Memoized to prevent effect re-runs in EmbeddedYouTubePlayer
  const handleYoutubePlayerReady = useCallback((player) => {
    setYoutubePlayer(player);
    if (onYoutubePlayerReady) onYoutubePlayerReady(player);
  }, [onYoutubePlayerReady]);

  // Handle YouTube state change - track actual playing state internally
  // Memoized to prevent effect re-runs in EmbeddedYouTubePlayer
  const handleYoutubeStateChange = useCallback((playing) => {
    setYoutubeActuallyPlaying(playing);
    if (onYoutubeStateChange) onYoutubeStateChange(playing);
  }, [onYoutubeStateChange]);

  // Auto-hide notifications
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), notification.duration || 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Reset panels when switching service
  useEffect(() => {
    setShowSearch(false);
    setShowVolumeControls(false);
  }, [service]);

  // Check if current service is active
  useEffect(() => {
    const checkServiceState = async () => {
      if (service === 'spotify' && spotifyPlayer) {
        if (spotifyTrack && spotifyTrack.name) {
          setIsActive(true);
          return;
        }
        try {
          const state = await spotifyPlayer.getCurrentState();
          setIsActive(!!state);
        } catch {
          setIsActive(true);
        }
      } else if (service === 'youtube' && youtubePlayer) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }
    };

    checkServiceState();
    const interval = setInterval(checkServiceState, 3000);
    return () => clearInterval(interval);
  }, [service, spotifyPlayer, youtubePlayer, spotifyTrack]);

  // Volume control
  const handleVolumeChange = async (newVolume) => {
    setVolume(newVolume);
    try {
      if (service === 'spotify' && spotifyPlayer?.setVolume) {
        await spotifyPlayer.setVolume(newVolume / 100);
      } else if (service === 'youtube' && youtubePlayer?.setVolume) {
        youtubePlayer.setVolume(newVolume);
      }
    } catch (error) {
      console.error('Error setting volume:', error);
      setNotification({
        type: 'error',
        message: `Error adjusting ${service} volume`,
        duration: 4000
      });
    }
  };

  const toggleVolumeControls = () => setShowVolumeControls(!showVolumeControls);
  const toggleSearch = () => setShowSearch(!showSearch);

  // Spotify controls
  const handleSpotifyAction = async (action) => {
    if (!spotifyPlayer) return;

    try {
      if (action === 'previous') await spotifyPlayer.previousTrack();
      if (action === 'toggle') await spotifyPlayer.togglePlay();
      if (action === 'next') await spotifyPlayer.nextTrack();
    } catch (error) {
      const message = error.message || '';
      if (message.includes('no list was loaded')) {
        setNotification({
          type: 'warning',
          message: 'Start playing a playlist or album in Spotify first',
          duration: 5000
        });
      } else if (message.includes('device not found')) {
        setNotification({
          type: 'error',
          message: 'Select "PomoDucky Player" as your active device in Spotify',
          duration: 8000
        });
      } else {
        setNotification({
          type: 'error',
          message: 'Spotify command failed',
          duration: 5000
        });
      }
    }
  };

  // YouTube controls
  const handleYoutubeTogglePlay = () => {
    if (!youtubePlayer) return;
    
    try {
      // Use the internal state which is locked to actual player events
      if (youtubeActuallyPlaying) {
        youtubePlayer.pause?.();
      } else {
        youtubePlayer.play?.();
      }
    } catch (error) {
      console.error('Error controlling YouTube player:', error);
      setNotification({
        type: 'error',
        message: 'Error controlling YouTube playback',
        duration: 4000
      });
    }
  };

  // Current content info
  const contentInfo = (() => {
    if (service === 'spotify' && !spotifyEnabled) {
      return {
        title: 'Spotify',
        artist: 'Login to enable Spotify controls',
        thumbnail: null,
        isPlaying: false
      };
    }
    if (service === 'spotify' && spotifyTrack) {
      return {
        title: spotifyTrack.name || 'No track playing',
        artist: spotifyTrack.artists?.map(a => a.name).join(', ') || 'Start playing music on Spotify',
        thumbnail: spotifyTrack.album?.images?.[0]?.url,
        isPlaying: !isSpotifyPaused
      };
    }
    if (service === 'youtube' && youtubeVideo) {
      return {
        title: youtubeVideo.snippet?.title || 'No video selected',
        artist: youtubeVideo.snippet?.channelTitle || 'Select a YouTube video',
        thumbnail: youtubeVideo.snippet?.thumbnails?.default?.url,
        isPlaying: youtubeActuallyPlaying // Use internal state locked to actual player
      };
    }
    return {
      title: service === 'spotify' ? 'No track playing' : 'No video selected',
      artist: service === 'spotify' ? 'Start playing music on Spotify' : 'Select a YouTube video',
      thumbnail: null,
      isPlaying: false
    };
  })();

  // Handlers per service
  const controls = service === 'spotify'
    ? {
        previous: () => handleSpotifyAction('previous'),
        togglePlay: () => handleSpotifyAction('toggle'),
        next: () => handleSpotifyAction('next')
      }
    : {
        previous: () => {},
        togglePlay: handleYoutubeTogglePlay,
        next: () => {}
      };

  return (
    <>
      {notification && (
        <div className={`player-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className={`floating-player ${showSearch ? 'has-search' : ''} ${service === 'youtube' && youtubeVideo?.id?.videoId ? 'youtube-video-active' : ''}`}>
        <div className="floating-player-tabs" role="tablist" aria-label="Media service">
          <button
            type="button"
            className={`floating-tab ${service === 'spotify' ? 'active' : ''}`}
            onClick={() => {
              if (service === 'spotify') return;
              onBeforeSwitchToSpotify?.();
              onServiceChange && onServiceChange('spotify');
            }}
            role="tab"
            aria-selected={service === 'spotify'}
            tabIndex={service === 'spotify' ? 0 : -1}
          >
            Spotify
          </button>
          <button
            type="button"
            className={`floating-tab ${service === 'youtube' ? 'active' : ''}`}
            onClick={() => {
              if (service === 'youtube') return;
              onBeforeSwitchToYoutube?.();
              onServiceChange && onServiceChange('youtube');
            }}
            role="tab"
            aria-selected={service === 'youtube'}
            tabIndex={service === 'youtube' ? 0 : -1}
          >
            YouTube
          </button>
        </div>

        {/* YouTube player - kept mounted but hidden when not on YouTube tab to avoid destroy errors */}
        {youtubeVideo?.id?.videoId && (
          <div style={{ display: service === 'youtube' ? 'block' : 'none' }}>
            <EmbeddedYouTubePlayer
              videoId={youtubeVideo.id.videoId}
              onPlayerReady={handleYoutubePlayerReady}
              onStateChange={handleYoutubeStateChange}
              volume={volume}
            />
          </div>
        )}
        
        {/* Show thumbnail or placeholder when not showing YouTube player */}
        {(service !== 'youtube' || !youtubeVideo?.id?.videoId) && (
          contentInfo.thumbnail ? (
            <img
              src={contentInfo.thumbnail}
              alt="Content thumbnail"
              className="floating-player-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="floating-player-cover placeholder">
              {service === 'spotify' ? '♫' : '▶'}
            </div>
          )
        )}

        <div className="floating-player-info">
          <div className="floating-player-title">{contentInfo.title}</div>
          <div className="floating-player-artist">{contentInfo.artist}</div>
          {!isActive && service === 'spotify' && (
            <div className="device-status warning">
              Select "PomoDucky Player" in Spotify for full control
            </div>
          )}
          <div className="service-indicator">
            {service === 'spotify' ? '♫ Spotify' : '▶ YouTube'}
          </div>
        </div>

        <div className="floating-player-controls">
          {service === 'spotify' && !spotifyEnabled ? (
            <>
              <Login />
              <button
                className="floating-btn volume-btn"
                onClick={toggleVolumeControls}
                title="Volume control"
                disabled
              >
                🔊
              </button>
            </>
          ) : (
            <>
          <button
            className="floating-btn"
            onClick={controls.previous}
            title="Previous"
            disabled={service === 'youtube'}
          >
            ⏮
          </button>
          <button
            className="floating-btn"
            onClick={controls.togglePlay}
            title={contentInfo.isPlaying ? 'Pause' : 'Play'}
          >
            {contentInfo.isPlaying ? '⏸' : '▶'}
          </button>
          <button
            className="floating-btn"
            onClick={controls.next}
            title="Next"
            disabled={service === 'youtube'}
          >
            ⏭
          </button>
          <button
            className={`floating-btn ${showSearch ? 'active' : ''}`}
            onClick={toggleSearch}
            title={service === 'spotify' ? 'Search Spotify' : 'Search YouTube'}
            disabled={service === 'spotify' && !spotifyEnabled}
          >
            🔎
          </button>
          <button
            className="floating-btn volume-btn"
            onClick={toggleVolumeControls}
            title="Volume control"
          >
            🔊
          </button>
            </>
          )}
        </div>

        {showVolumeControls && (
          <div className="volume-controls-popup">
            <label htmlFor="volume-slider">
              {service === 'spotify' ? 'Spotify' : 'YouTube'} Volume: {volume}%
            </label>
            <input
              type="range"
              id="volume-slider"
              className="volume-slider"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
            />
          </div>
        )}

        {service === 'spotify' && showSearch && (
          <div className="floating-player-search">
            <SpotifySearch
              deviceId={spotifyDeviceId}
              disabled={!spotifyEnabled}
              variant="compact"
              onPlayed={() => setShowSearch(false)}
            />
          </div>
        )}

        {service === 'youtube' && showSearch && (
          <div className="floating-player-search">
            <YouTubeSearch
              variant="compact"
              disabled={false}
              apiError={youtubeApiError}
              onSearchError={onYoutubeSearchError}
              onVideoSelect={(video) => {
                if (onYoutubeVideoSelect) onYoutubeVideoSelect(video);
              }}
              onSelected={() => setShowSearch(false)}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default UnifiedFloatingPlayer;
